"use client";

import type { CampaignQuestState } from "@/components/QuestsV2/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import Image from "next/image";
import * as React from "react";

export interface BadgeTooltipData {
  title: string;
  description?: string;
  completionDate?: string;
  gameName?: string;
}

interface DisplayBadgeProps {
  imageUrl: string;
  alt: string;
  tooltipText?: string;
  tooltipData?: BadgeTooltipData;
  size?: number;
  className?: string;
  animated?: boolean;
  quest?: CampaignQuestState;
  onBadgeHover?: (quest: CampaignQuestState) => void;
  onBadgeClick?: (quest: CampaignQuestState) => void;
}

export const DisplayBadge = React.forwardRef<
  HTMLButtonElement,
  DisplayBadgeProps
>(
  (
    {
      imageUrl,
      alt,
      tooltipText,
      tooltipData,
      size = 24,
      className,
      animated = false,
      quest,
      onBadgeHover,
      onBadgeClick,
    },
    ref,
  ) => {
    const { trackV3 } = useAnalytics();
    const [isOpen, setIsOpen] = React.useState(false);

    const formatCompletionDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    };

    const handleMouseEnter = React.useCallback(() => {
      if (quest && onBadgeHover) {
        onBadgeHover(quest);
      }
    }, [quest, onBadgeHover]);

    const handleClick = React.useCallback(
      (e: React.MouseEvent) => {
        if (quest && onBadgeClick) {
          e.stopPropagation();
          onBadgeClick(quest);
        }
        setIsOpen((prev) => !prev);
      },
      [quest, onBadgeClick],
    );

    const handleTooltipOpenChange = React.useCallback(
      (open: boolean) => {
        setIsOpen(open);
        if (open && quest) {
          trackV3({
            object: "Quest",
            action: Action.VIEWED,
            extras: {
              gameId: quest.gameId,
              questId: quest.externalId,
              questName: quest.name,
              questType: quest.type,
              isCompleted: quest.completed,
              section: "Badge",
            },
          });
        }
      },
      [trackV3, quest],
    );

    const renderTooltipContent = () => {
      if (tooltipData) {
        return (
          <div className="text-center space-y-1 py-2">
            <div className="font-bold text-purple-400 text-base">
              {tooltipData.title}
            </div>
            {(tooltipData.gameName || tooltipData.description) && (
              <div className="text-sm text-gray-200">
                {tooltipData.gameName ? (
                  <>
                    Esteemed member of the{" "}
                    <span className="font-bold">{tooltipData.gameName}</span>{" "}
                    Discord
                  </>
                ) : (
                  tooltipData.description
                )}
              </div>
            )}
            {tooltipData.completionDate && (
              <div className="text-xs text-gray-400 pt-1">
                Unlocked {formatCompletionDate(tooltipData.completionDate)}
              </div>
            )}
          </div>
        );
      }
      return <p>{tooltipText}</p>;
    };

    const imageClassName = animated
      ? "object-contain w-full h-full quest-badge-glow"
      : "object-contain";

    return (
      <TooltipProvider>
        <Tooltip
          open={isOpen}
          onOpenChange={quest ? handleTooltipOpenChange : setIsOpen}
        >
          <TooltipTrigger asChild>
            <button
              ref={ref}
              type="button"
              className={`border-0 bg-transparent p-0 cursor-pointer ${className || ""}`}
              onClick={handleClick}
              onMouseEnter={handleMouseEnter}
            >
              {animated && (
                <style>{`
                  @keyframes pulse-glow {
                    0%, 100% {
                      filter: brightness(1.3) contrast(1.2) drop-shadow(0 0 12px rgba(147, 51, 234, 1)) drop-shadow(0 0 24px rgba(147, 51, 234, 0.6)) drop-shadow(0 0 32px rgba(147, 51, 234, 0.3));
                    }
                    50% {
                      filter: brightness(1.6) contrast(1.4) drop-shadow(0 0 20px rgba(147, 51, 234, 1)) drop-shadow(0 0 40px rgba(147, 51, 234, 1)) drop-shadow(0 0 60px rgba(147, 51, 234, 0.8));
                    }
                  }
                  .quest-badge-glow {
                    animation: pulse-glow 1s ease-in-out infinite;
                  }
                `}</style>
              )}
              <Image
                src={imageUrl}
                alt={alt}
                width={size}
                height={size}
                className={imageClassName}
                style={
                  !animated
                    ? {
                        filter:
                          "brightness(1.3) contrast(1.2) drop-shadow(0 0 8px rgba(147, 51, 234, 0.8)) drop-shadow(0 0 16px rgba(147, 51, 234, 0.4))",
                      }
                    : undefined
                }
              />
            </button>
          </TooltipTrigger>
          <TooltipPrimitive.Portal>
            <TooltipContent className="z-[9999] max-w-xs">
              {renderTooltipContent()}
            </TooltipContent>
          </TooltipPrimitive.Portal>
        </Tooltip>
      </TooltipProvider>
    );
  },
);

DisplayBadge.displayName = "DisplayBadge";
